'use client';

import classnames from 'classnames';
import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import { SlotItem, SlottedLayout } from '@stadionhq-v3/base-ui';
import { Panel, PANELS } from '@matchCentreConfig';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSingleMediaQuery } from '@stadionhq-v3/hooks';
import {
  getMatchStatus,
  OptaMatchStatus,
  T_SHIRT_SIZES,
} from '@stadionhq-v3/core';
// eslint-disable-next-line no-restricted-imports
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { MatchCentreHeader } from '@components/live/match-centre-header';
import { useMatchCentreLayoutContext } from '@stadionhq-v3/football-ui';
import styles from './matchCentreLayout.module.scss';
import { MatchCentreLayoutProps } from './matchCentreLayout.types';
import { QA_MATCH_CENTRE_LAYOUT } from '../../../testConfig/testIds/organismsTestIds';

const MatchCentreLayout = ({
  matchHeaderProps,
  testId = QA_MATCH_CENTRE_LAYOUT.COMPONENT,
  classNames = [],
  nativeProps = {},
  locale,
  ...props
}: MatchCentreLayoutProps) => {
  const classes = classnames([styles.matchCentreLayout], classNames);

  const { media } = useSingleMediaQuery({
    breakpoint: { size: T_SHIRT_SIZES.L },
  });

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const tab = searchParams.get('tab');

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams);
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  const matchStatus = getMatchStatus(
    matchHeaderProps?.componentProps?.matchStatus ||
      (matchHeaderProps?.matchHeader?.matchStatus as OptaMatchStatus)
  );

  const {
    state,
    dispatch,
    actions: { setRenders, setBreakpoint },
    computed,
  } = useMatchCentreLayoutContext<Panel>();

  useEffect(() => {
    setBreakpoint && setBreakpoint({ state, dispatch }, media.matches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, setBreakpoint]);

  useEffect(() => {
    Object.keys(PANELS).forEach((key) => {
      const k: keyof typeof PANELS = key as keyof typeof PANELS;
      if (!Object.hasOwn(props, camelCase(k))) return;

      setRenders &&
        setRenders(
          { state, dispatch },
          {
            slotName: PANELS[k],
            // @ts-expect-error no way to check prop key
            render: () => props[camelCase(k)],
          }
        );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRenders]);

  const main: SlotItem | undefined = useMemo(() => {
    if (!computed) return undefined;

    const computedMain = computed!(state).main;

    return computedMain
      ? {
          ...computedMain,
          ...(tab && computedMain.tabs
            ? {
                selectedTabSlotName: computedMain.tabs.find(
                  ({ slotName }: SlotItem) => kebabCase(slotName) === tab
                )?.slotName,
              }
            : {}),
          changeTabCallBack: (i: number) => {
            if (!computedMain.tabs[i]?.slotName) return;

            router.replace(
              `${pathname}?${createQueryString('tab', kebabCase(computedMain.tabs[i]?.slotName))}`,
              { scroll: false }
            );
          },
        }
      : undefined;
  }, [computed, createQueryString, pathname, router, state, tab]);

  /**
   * This will set the tab to suit the match status
   */
  useEffect(() => {
    if (tab || !main || !main?.tabs || !main?.changeTabCallBack) {
      return;
    }

    let selectedTabIndex = main.tabs.findIndex(
      ({ slotName }) => slotName === matchStatus
    );

    /**
     * If the corresponding match status has no content, use the preceding match status
     */
    if (selectedTabIndex === -1 && main.tabs.length > 0) {
      selectedTabIndex = main.tabs.length - 1;
    }

    if (selectedTabIndex >= 0) {
      main.changeTabCallBack(selectedTabIndex);
    }
  }, [main, matchStatus, tab]);

  return (
    <div {...nativeProps} className={classes} data-testid={testId}>
      <SlottedLayout>
        <SlottedLayout.Header>
          {matchHeaderProps ? (
            <MatchCentreHeader {...matchHeaderProps} locale={locale} />
          ) : (
            <></>
          )}
        </SlottedLayout.Header>
        {main ? <SlottedLayout.Main {...main} contain /> : <></>}
      </SlottedLayout>
    </div>
  );
};

export default MatchCentreLayout;
